import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const API = API_BASE_URL;

const TournamentContext = createContext();

export function TournamentProvider({ children }) {
  const [selectedTournamentId, setSelectedTournamentId] = useState(
    localStorage.getItem('selectedTournamentId') || null
  );
  const [tournaments, setTournaments] = useState([]);
  const [currentTournament, setCurrentTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch all available tournaments
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        // Try admin endpoint first (gets all tournaments including archived)
        let response;
        try {
          response = await axios.get(`${API}/admin/tournaments`, {
            withCredentials: true
          });
        } catch (adminError) {
          // If not admin, use public tournaments endpoint (gets all active tournaments)
          try {
            response = await axios.get(`${API}/tournaments`);
          } catch (publicError) {
            console.error('No tournaments available');
            setLoading(false);
            return;
          }
        }

        // Ensure response.data is an array
        if (!Array.isArray(response.data)) {
          console.error('Tournament data is not an array:', response.data);
          setLoading(false);
          return;
        }

        const activeTournaments = response.data.filter(
          t => t.status === 'picking' || t.status === 'active' || t.status === 'setup'
        );
        setTournaments(activeTournaments);

        // If no tournament is selected, select the first active one
        if (!selectedTournamentId && activeTournaments.length > 0) {
          const firstTournament = activeTournaments[0];
          setSelectedTournamentId(firstTournament.tournament_id);
          localStorage.setItem('selectedTournamentId', firstTournament.tournament_id);
        }
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTournaments();
  }, []);

  // Fetch current tournament details when selection changes
  useEffect(() => {
    const fetchCurrentTournament = async () => {
      if (!selectedTournamentId) return;

      try {
        const response = await axios.get(
          `${API}/tournaments/${selectedTournamentId}`,
          { withCredentials: true }
        );
        setCurrentTournament(response.data);
      } catch (error) {
        console.error('Failed to fetch current tournament:', error);
      }
    };

    fetchCurrentTournament();
  }, [selectedTournamentId]);

  const selectTournament = (tournamentId) => {
    setSelectedTournamentId(tournamentId);
    localStorage.setItem('selectedTournamentId', tournamentId);
    // Reload page to refresh all data
    window.location.reload();
  };

  return (
    <TournamentContext.Provider
      value={{
        selectedTournamentId,
        tournaments,
        currentTournament,
        selectTournament,
        loading
      }}
    >
      {children}
    </TournamentContext.Provider>
  );
}

export function useTournament() {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error('useTournament must be used within TournamentProvider');
  }
  return context;
}
